import { CommonModule } from '@angular/common';
import { Component, HostListener, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { firstValueFrom } from 'rxjs';

import { SelfServiceConfigService } from '../shared/self-service-config.service';
import { SelfServiceApiConfigService } from '../shared/self-service-api-config.service';
import { SelfServiceUiService } from '../shared/self-service-ui.service';
import { resolveSelfServiceApiUrl } from '../shared/self-service-api.utils';
import { BuilderLayoutConfig } from '../shared/self-service-config.types';

interface RepoWorkspaceForm {
  repository: string;
  baseBranch: string;
  newBranchName: string;
  serviceName: string;
}

/** The live-streamed code-generation result card (repository setup happens
 *  silently in the background — only its success/failure toast is shown; the
 *  streamed generation output is the only thing rendered in the UI). */
interface GeneratedMapEntry {
  title: string;
  type: 'code';
  content: string;
}

interface RepositorySetupResult {
  submodulePath: string;
  serviceName: string;
}

interface ParsedSseEvent {
  event: string;
  data: string;
}

/**
 * Kaveri Generation builder — "Repository & Workspace Configuration" step of
 * the DVM Visualization Tool's config-driven builder family (see
 * data-builder / dvm-layout-builder siblings). Drives the real backend
 * pipeline in kaveriBackend/spriced-platform-data-management-layer/kaveri-lite:
 * RepositorySetupController.setupRepository() creates the branch/workspace,
 * then JavaStreamingController.streamJavaGeneration() streams the generated
 * Java service class from the uploaded TBRD YAML.
 */
@Component({
  selector: 'app-kaveri-generate-builder',
  standalone: true,
  imports: [CommonModule, FormsModule, MatButtonModule, MatIconModule],
  templateUrl: './kaveri-generate-builder.component.html',
  styleUrl: './kaveri-generate-builder.component.scss',
})
export class KaveriGenerateBuilderComponent implements OnInit {
  private readonly http = inject(HttpClient);
  private readonly configService = inject(SelfServiceConfigService);
  private readonly apiConfigService = inject(SelfServiceApiConfigService);
  private readonly ui = inject(SelfServiceUiService);

  static readonly STEP_FIELD_IDS: ReadonlySet<string> = new Set<string>(['repoWorkspaceConfig']);

  private static readonly DEFAULT_LAYOUT: BuilderLayoutConfig = {
    version: '1.0',
    builder: 'kaveri-generate',
    steps: [
      {
        id: 'repo-workspace',
        label: 'Repository & Workspace',
        sections: [
          {
            id: 'repoWorkspaceConfig',
            title: 'Repository & Workspace Configuration',
            fields: [{ field: 'repoWorkspaceConfig' }],
          },
        ],
      },
    ],
    options: {
      repositories: [{ label: 'application-interface (GitHub)', value: 'application-interface' }],
      acceptedTbrdFileTypes: '.yaml,.yml',
    },
    defaults: { repository: 'application-interface' },
    apiEndpoints: {
      baseUrl: '',
      // Confirmed against RepositorySetupController.fetchBranches() — GET /spriced/platform/branches.
      branchesApi: '/branches',
      // RepositorySetupController.setupRepository() — POST /spriced/platform/setup.
      setupApi: '/setup',
      // JavaStreamingController.streamJavaGeneration() — POST /spriced/platform/generate-java-stream.
      processDocxApi: '/generate-java-stream',
      generateDvmApi: '/agent/generate-dvm',
      deamReadsApi: '/deam-reads/process',
    },
  };

  layout: BuilderLayoutConfig = KaveriGenerateBuilderComponent.DEFAULT_LAYOUT;

  form: RepoWorkspaceForm = {
    repository: '',
    baseBranch: '',
    newBranchName: '',
    serviceName: '',
  };

  branches: string[] = [];
  branchesLoading = false;
  tbrdFile: File | null = null;

  generatedMaps: GeneratedMapEntry[] = [];
  isProcessing = false;
  processingStatusText = '';
  openDropdown: 'repository' | 'baseBranch' | null = null;

  get repositories() {
    return this.layout.options?.repositories ?? [];
  }

  get acceptedTbrdFileTypes(): string {
    return this.layout.options?.acceptedTbrdFileTypes || '.yaml,.yml';
  }

  get selectedRepositoryLabel(): string {
    return this.repositories.find((repo) => repo.value === this.form.repository)?.label || 'Select repository';
  }

  get selectedBaseBranchLabel(): string {
    if (this.branchesLoading) return 'Loading branches...';
    return this.form.baseBranch || 'Select Base Branch';
  }

  get canProceed(): boolean {
    return !this.isProcessing && !!this.form.baseBranch && this.form.newBranchName.trim().length > 0 && !!this.tbrdFile;
  }

  @HostListener('document:click')
  closeDropdowns(): void {
    this.openDropdown = null;
  }

  ngOnInit(): void {
    this.configService.loadBuilderLayout('kaveri-generate', KaveriGenerateBuilderComponent.DEFAULT_LAYOUT).subscribe((layout) => {
      const validation = this.configService.validateLayout(layout, KaveriGenerateBuilderComponent.STEP_FIELD_IDS);
      if (validation.unknownFields.length) {
        console.warn('[kaveri-generate-builder] config references unknown field ids; they will be skipped:', validation.unknownFields);
      }
      this.layout = layout;
      this.apiConfigService.applyFromLayout(layout);
      this.form.repository = this.layout.defaults?.repository || this.repositories[0]?.value || '';
      this.loadBranches();
    });
  }

  private loadBranches(): void {
    this.branchesLoading = true;
    const url = resolveSelfServiceApiUrl(this.layout.apiEndpoints?.branchesApi, '/branches');
    this.http.get<string[]>(url).subscribe({
      next: (branches) => {
        this.branches = Array.isArray(branches) ? branches : [];
        this.branchesLoading = false;
      },
      error: (error) => {
        console.error('[kaveri-generate-builder] failed to load branches', error);
        this.ui.showError('Failed to load branches from the repository.');
        this.branches = [];
        this.branchesLoading = false;
      },
    });
  }

  onTbrdFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.tbrdFile = input.files?.[0] ?? null;
  }

  toggleDropdown(event: MouseEvent, dropdown: 'repository' | 'baseBranch'): void {
    event.stopPropagation();
    if (dropdown === 'baseBranch' && this.branchesLoading) return;
    this.openDropdown = this.openDropdown === dropdown ? null : dropdown;
  }

  selectRepository(event: MouseEvent, repository: string): void {
    event.stopPropagation();
    this.form.repository = repository;
    this.openDropdown = null;
  }

  selectBaseBranch(event: MouseEvent, branch: string): void {
    event.stopPropagation();
    this.form.baseBranch = branch;
    this.openDropdown = null;
  }

  async proceedToCodeGeneration(): Promise<void> {
    if (!this.canProceed || !this.tbrdFile) return;

    this.isProcessing = true;
    this.generatedMaps = [];
    this.processingStatusText = 'Setting up repository workspace…';

    // 1. Repository & workspace setup — mirrors kaveri.html: the response (or
    // its error) is written straight into the UI immediately, and a setup
    // failure aborts the whole pipeline (no point streaming code with no
    // workspace to save it into).
    let setupResult: RepositorySetupResult;
    try {
      setupResult = await this.setupWorkspace();
      this.ui.showSuccess(`Repository workspace ready at ${setupResult.submodulePath}.`);
    } catch (error) {
      const message = this.extractHttpErrorMessage(error);
      this.ui.showError(`Workspace setup failed: ${message}`);
      this.processingStatusText = 'Workspace setup failed.';
      this.isProcessing = false;
      return;
    }

    // 2. Stream code generation from the TBRD YAML, updating the SAME result
    // card live as method/progress/error events arrive.
    const fileTitle = (setupResult.serviceName || this.form.serviceName || this.form.newBranchName).trim() || 'GeneratedService';
    const codeTitle = `${fileTitle}.java`;

    try {
      this.processingStatusText = 'Reading TBRD document…';
      const tbrdYamlText = await this.readFileAsText(this.tbrdFile);

      this.processingStatusText = 'Generating code… 0%';
      this.upsertResult('code', codeTitle, '// 📡 Connection established. Awaiting initial code generation packet…\n');

      const generatedCode = await this.streamCodeGeneration(tbrdYamlText, (liveLog) => this.upsertResult('code', codeTitle, liveLog));

      this.upsertResult('code', codeTitle, generatedCode);
      this.ui.showSuccess('Code generation complete.');
      this.processingStatusText = 'Code generation complete.';
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Code generation failed. Check the backend connection and try again.';
      console.error('[kaveri-generate-builder] code generation failed', error);
      this.appendResult('code', codeTitle, `\n\n// ❌ FATAL ERROR: ${message}\n// Connection disrupted.`);
      this.ui.showError(message);
      this.processingStatusText = 'Code generation failed.';
    } finally {
      this.isProcessing = false;
    }
  }

  /** POST { baseBranch, newBranchName, serviceName } to RepositorySetupController.setupRepository(). */
  private setupWorkspace(): Promise<RepositorySetupResult> {
    const url = resolveSelfServiceApiUrl(this.layout.apiEndpoints?.setupApi, '/setup');
    const serviceName = this.form.serviceName.trim() || this.form.newBranchName.trim();
    return firstValueFrom(
      this.http.post<RepositorySetupResult>(url, {
        baseBranch: this.form.baseBranch,
        newBranchName: this.form.newBranchName.trim(),
        serviceName,
      }),
    );
  }

  /** Create (or replace the content of) the result card for `type`+`title`. */
  private upsertResult(type: GeneratedMapEntry['type'], title: string, content: string): void {
    const index = this.generatedMaps.findIndex((entry) => entry.type === type && entry.title === title);
    if (index === -1) {
      this.generatedMaps = [...this.generatedMaps, { title, type, content }];
      return;
    }
    const next = [...this.generatedMaps];
    next[index] = { ...next[index], content };
    this.generatedMaps = next;
  }

  /** Append to an existing result card's content (creates it if absent). */
  private appendResult(type: GeneratedMapEntry['type'], title: string, extra: string): void {
    const existing = this.generatedMaps.find((entry) => entry.type === type && entry.title === title);
    this.upsertResult(type, title, (existing?.content ?? '') + extra);
  }

  /** Angular's HttpErrorResponse wraps RepositorySetupController's error body
   *  ({"error": "..."}) under `.error` — surface that message instead of a
   *  generic "Http failure response for ...". */
  private extractHttpErrorMessage(error: unknown): string {
    if (error && typeof error === 'object') {
      const httpError = error as { error?: unknown; message?: string };
      const backendError = httpError.error;
      if (backendError && typeof backendError === 'object' && 'error' in (backendError as Record<string, unknown>)) {
        return String((backendError as Record<string, unknown>)['error']);
      }
      if (typeof backendError === 'string' && backendError.trim()) return backendError;
      if (httpError.message) return httpError.message;
    }
    return error instanceof Error ? error.message : 'Unknown error.';
  }

  private readFileAsText(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result ?? ''));
      reader.onerror = () => reject(reader.error ?? new Error('Failed to read the TBRD file.'));
      reader.readAsText(file);
    });
  }

  /** POST the raw TBRD YAML (text/plain) to JavaStreamingController.streamJavaGeneration() and
   *  consume its text/event-stream response — mirrors kaveri.html's processSseEvent(): "progress"
   *  events update the status text, "method" events APPEND onto a running log (reported live via
   *  onLiveLog so the UI shows generation happening method-by-method), "complete" carries the full
   *  generated Java class (replaces the log entirely), "error" aborts the stream. HttpClient has no
   *  SSE-over-POST support, so this reads the fetch body stream directly. */
  private async streamCodeGeneration(tbrdYamlText: string, onLiveLog: (log: string) => void): Promise<string> {
    const url = resolveSelfServiceApiUrl(this.layout.apiEndpoints?.processDocxApi, '/generate-java-stream');
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'text/plain' },
      body: tbrdYamlText,
    });
    if (!response.ok || !response.body) {
      throw new Error(`Code generation request failed (${response.status}).`);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let liveLog = '// 📡 Connection established. Awaiting initial code generation packet…\n\n';
    let completeClass = '';

    for (;;) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      let boundary = buffer.indexOf('\n\n');
      while (boundary !== -1) {
        const parsed = this.parseSseEvent(buffer.slice(0, boundary));
        buffer = buffer.slice(boundary + 2);

        if (parsed?.event === 'progress') {
          this.processingStatusText = `Generating code… ${parsed.data}%`;
        } else if (parsed?.event === 'method') {
          liveLog += parsed.data.replace(/\\n/g, '\n') + '\n\n';
          onLiveLog(liveLog);
        } else if (parsed?.event === 'error') {
          throw new Error(parsed.data || 'Code generation failed.');
        } else if (parsed?.event === 'complete') {
          completeClass = parsed.data.replace(/\\n/g, '\n');
        }

        boundary = buffer.indexOf('\n\n');
      }
    }

    if (!completeClass) {
      throw new Error('Code generation stream ended without a completed class.');
    }
    return completeClass;
  }

  private parseSseEvent(raw: string): ParsedSseEvent | null {
    let event = 'message';
    const dataLines: string[] = [];
    for (const line of raw.split('\n')) {
      if (line.startsWith('event:')) event = line.slice('event:'.length).trim();
      else if (line.startsWith('data:')) dataLines.push(line.slice('data:'.length).trim());
    }
    return dataLines.length ? { event, data: dataLines.join('\n') } : null;
  }
}
